import {
  Button,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
} from "@material-ui/core";
import { Search } from "@material-ui/icons";
import DateFnsUtils from "@date-io/date-fns";
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import { addDays, max, min, parseISO, startOfDay } from "date-fns";
import { useState } from "react";
import { useHistory } from "react-router";
import cotizador from "../../../../../services/cotizador";
import { Spinner } from "../../../../../shared/components";
import useStyles from "./styles";

const fechaLocal = (value) =>
  startOfDay(typeof value === "string" ? parseISO(value) : value);

const formatDate = (value) => {
  if (!value) return "";
  const date = fechaLocal(value);
  if (Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const CotizacionForm = ({ setMessage, setShowMessage }) => {
  const classes = useStyles();
  const history = useHistory();
  const [grupo, setGrupo] = useState("");
  const [errorGrupo, setErrorGrupo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [grupoInfo, setGrupoInfo] = useState({
    contratante: "",
    finVigencia: "",
  });
  const [inicioVigencia, setInicioVigencia] = useState(null);
  const [errorInicio, setErrorInicio] = useState("");
  const [rangoInicio, setRangoInicio] = useState({ min: null, max: null });
  const [grupoEncontrado, setGrupoEncontrado] = useState(false);
  const [grupoBuscado, setGrupoBuscado] = useState("");

  const handleChangeGrupo = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    setGrupo(value);
    if (value.length !== 5) setErrorGrupo(false);
    if (value !== grupoBuscado) {
      setGrupoEncontrado(false);
      setGrupoInfo({ contratante: "", finVigencia: "" });
      setInicioVigencia(null);
      setRangoInicio({ min: null, max: null });
      setErrorInicio("");
      setErrorGrupo(false);
    }
  };

  const handleSearch = async () => {
    if (grupo.length !== 5) {
      setErrorGrupo(true);
      return;
    }
    setGrupoBuscado(grupo);
    setErrorGrupo(false);
    setLoading(true);
    const response = await cotizador.consultaGrupo(grupo);

    if (response.data?.codigo === 1) {
      const fechaInicio = response.data.fechaInicio;
      const fechaFin = response.data.fechafinal;
      setGrupoInfo({
        contratante: response.data.client?.personalData?.businessName || "",
        finVigencia: formatDate(fechaFin),
      });
      const hoy = startOfDay(new Date());
      setRangoInicio({
        min: max([fechaLocal(fechaInicio), addDays(hoy, -15)]),
        max: min([fechaLocal(fechaFin), addDays(hoy, 15)]),
      });
      setInicioVigencia(null);
      setErrorInicio("");
      setGrupoEncontrado(true);
    } else {
      setGrupoInfo({ contratante: "", finVigencia: "" });
      setInicioVigencia(null);
      setErrorInicio("");
      setRangoInicio({ min: null, max: null });
      setGrupoEncontrado(false);
      setMessage({
        text:
          response.error && !response.data
            ? "Ocurrió un error con su solicitud"
            : response.data?.mensaje ||
              "No se encontró información para el grupo ingresado, verifica que el número sea correcto.",
        severity: "warning",
      });
      setShowMessage(true);
    }
    setLoading(false);
  };

  const handleChangeInicio = (date) => {
    setInicioVigencia(date);
  };

  const handleErrorInicio = (error) => {
    if (!error) {
      setErrorInicio("");
      return;
    }
    if (error === "invalidDate") {
      setErrorInicio("Formato de fecha inválido (DD/MM/AAAA)");
      return;
    }
    if (error === "minDate") {
      setErrorInicio(
        `La fecha debe ser igual o posterior al ${formatDate(rangoInicio.min)}`
      );
      return;
    }
    if (error === "maxDate") {
      setErrorInicio(
        `La fecha debe ser igual o anterior al ${formatDate(rangoInicio.max)}`
      );
      return;
    }
  };

  const inicioValido = () => {
    if (!inicioVigencia || !rangoInicio.min || !rangoInicio.max) return false;
    const fecha = fechaLocal(inicioVigencia);
    return fecha >= rangoInicio.min && fecha <= rangoInicio.max;
  };

  const handleBuscar = () => {
    if (!grupoEncontrado || !inicioValido()) return;
    history.push(
      `/alta-flotillas?grupo=${grupo}&inicioVigencia=${formatDate(
        inicioVigencia
      )}`
    );
  };

  return (
    <div className={classes.formContainer}>
      {loading && <Spinner />}
      <div>
        <InputLabel className={classes.label} htmlFor="grupo">
          Grupo
        </InputLabel>
        <TextField
          onChange={handleChangeGrupo}
          id="grupo"
          name="grupo"
          fullWidth
          size="small"
          variant="outlined"
          value={grupo}
          disabled={loading}
          error={errorGrupo}
          helperText={errorGrupo ? "Se requieren 5 dígitos" : ""}
          inputProps={{ maxLength: 5 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSearch}
                  disabled={loading}
                  aria-label="Buscar grupo"
                  size="small"
                  className={classes.searchIcon}
                >
                  <Search />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </div>
      <div>
        <InputLabel className={classes.label} htmlFor="contratante">
          Contratante
        </InputLabel>
        <TextField
          id="contratante"
          name="contratante"
          fullWidth
          size="small"
          variant="outlined"
          value={grupoInfo.contratante}
          inputProps={{ readOnly: true }}
        />
      </div>
      <div>
        <InputLabel className={classes.label} htmlFor="inicioVigencia">
          Inicio de Vigencia
        </InputLabel>
        <MuiPickersUtilsProvider utils={DateFnsUtils}>
          <KeyboardDatePicker
            id="inicioVigencia"
            name="inicioVigencia"
            fullWidth
            size="small"
            inputVariant="outlined"
            format="dd/MM/yyyy"
            placeholder="DD/MM/AAAA"
            value={inicioVigencia || null}
            minDate={rangoInicio.min || undefined}
            maxDate={rangoInicio.max || undefined}
            minDateMessage="minDate"
            maxDateMessage="maxDate"
            invalidDateMessage="invalidDate"
            disabled={!grupoEncontrado}
            error={Boolean(errorInicio)}
            helperText={errorInicio || ""}
            autoOk
            onChange={handleChangeInicio}
            onError={handleErrorInicio}
          />
        </MuiPickersUtilsProvider>
      </div>
      <div>
        <InputLabel className={classes.label} htmlFor="finVigencia">
          Fin de Vigencia
        </InputLabel>
        <TextField
          id="finVigencia"
          name="finVigencia"
          fullWidth
          size="small"
          variant="outlined"
          value={grupoInfo.finVigencia}
          inputProps={{ readOnly: true }}
        />
      </div>
      <Button
        variant="contained"
        className={classes.submitBottom}
        fullWidth
        type="button"
        disabled={!grupoEncontrado || !inicioValido() || loading}
        onClick={handleBuscar}
      >
        <strong>Buscar</strong>
      </Button>
    </div>
  );
};

export default CotizacionForm;
